import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowDown, ArrowUp, Megaphone, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PLACEMENT_LABELS = {
  top_banner: "Top Banner (Home)", sidebar_card: "Sidebar Card (Home)", mid_banner: "Mid-Page Banner (Home)",
  local_service_card: "Local Service (Home + Sponsors)", lower_banner: "Lower Banner (Home)",
};

export function SponsorPlacementsPanel({ job, onChanged }) {
  const [advertisers, setAdvertisers] = useState([]);
  const [placements, setPlacements] = useState(job.delivery?.sponsor_placements || []);
  const [draftAdvertiserId, setDraftAdvertiserId] = useState("");
  const [draftPlacementType, setDraftPlacementType] = useState("sidebar_card");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/advertisers").then((r) => setAdvertisers(r.data)).catch(() => {}); }, []);
  useEffect(() => { setPlacements(job.delivery?.sponsor_placements || []); }, [job.delivery?.sponsor_placements]);

  const byId = Object.fromEntries(advertisers.map((a) => [a.id, a]));
  const persist = async (next) => {
    setPlacements(next);
    setSaving(true);
    try {
      await api.put(`/admin/orders/${job.id}/sponsor-placements`, { placements: next.map((p) => ({ advertiser_id: p.advertiser_id, placement_type: p.placement_type })) });
      onChanged();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const add = () => {
    if (!draftAdvertiserId) { toast.error("Pick an advertiser first"); return; }
    persist([...placements, { advertiser_id: draftAdvertiserId, placement_type: draftPlacementType }]);
    setDraftAdvertiserId("");
  };
  const remove = (index) => persist(placements.filter((_, i) => i !== index));
  const move = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= placements.length) return;
    const next = [...placements];
    [next[index], next[target]] = [next[target], next[index]];
    persist(next);
  };

  return (
    <div className="rounded-xl border border-[#E4E9F0] bg-white p-5" style={{ boxShadow: "0 3px 12px rgba(15,23,42,0.035)" }} data-testid="sponsor-placements-panel">
      <div className="flex items-center gap-2 text-[#0C2347]"><Megaphone size={15} /><div className="text-[11px] font-mono uppercase tracking-[0.14em] text-[#475467]">Sponsor Placements</div></div>
      <p className="text-xs text-[#475467] mt-1">Assign local-service advertisers to this property website. Manage the advertiser directory from the Advertisers page.</p>

      <div className="mt-4 space-y-2">
        {placements.length === 0 && <div data-testid="sponsor-placements-empty" className="text-xs text-[#94A3B8] py-3">No sponsors assigned to this property yet.</div>}
        {placements.map((p, i) => {
          const ad = byId[p.advertiser_id];
          return (
            <div key={`${p.advertiser_id}-${p.placement_type}-${i}`} data-testid={`sponsor-placement-row-${i}`} className="flex items-center gap-3 rounded-lg border border-[#E4E9F0] px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#101828] truncate">{ad?.business_name || "Unknown advertiser"}</div>
                <div className="text-[11px] text-[#475467] font-mono">{PLACEMENT_LABELS[p.placement_type] || p.placement_type}</div>
              </div>
              <button data-testid={`sponsor-placement-up-${i}`} disabled={i === 0 || saving} onClick={() => move(i, -1)} className="p-1.5 text-[#94A3B8] disabled:opacity-30"><ArrowUp size={14} /></button>
              <button data-testid={`sponsor-placement-down-${i}`} disabled={i === placements.length - 1 || saving} onClick={() => move(i, 1)} className="p-1.5 text-[#94A3B8] disabled:opacity-30"><ArrowDown size={14} /></button>
              <button data-testid={`sponsor-placement-remove-${i}`} disabled={saving} onClick={() => remove(i)} className="p-1.5 text-red-600"><Trash2 size={14} /></button>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-[#E4E9F0] flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[160px]">
          <Select value={draftAdvertiserId} onValueChange={setDraftAdvertiserId}>
            <SelectTrigger data-testid="sponsor-placement-advertiser-select" className="rounded-lg border-[#E4E9F0] h-10 text-sm"><SelectValue placeholder="Choose advertiser" /></SelectTrigger>
            <SelectContent>{advertisers.filter((a) => a.enabled !== false).map((a) => <SelectItem key={a.id} value={a.id}>{a.business_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Select value={draftPlacementType} onValueChange={setDraftPlacementType}>
            <SelectTrigger data-testid="sponsor-placement-type-select" className="rounded-lg border-[#E4E9F0] h-10 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(PLACEMENT_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button data-testid="sponsor-placement-add" size="sm" disabled={saving} onClick={add} className="rounded-lg bg-[#0C2347] hover:bg-[#123262] text-white h-10 gap-1.5"><Plus size={14} />Add</Button>
      </div>
      {advertisers.length === 0 && <p className="text-xs text-[#94A3B8] mt-3">No advertisers in your directory yet — add one from the Advertisers page in the sidebar.</p>}
    </div>
  );
}
